import { type RefObject, useEffect } from "react";

/** Éléments qui reçoivent la tabulation. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/** Éléments tabulables visibles d'un conteneur, dans l'ordre du document. */
export function focusableIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.closest("[inert]") && el.getAttribute("aria-hidden") !== "true" && el.getClientRects().length > 0,
  );
}

/** Focus d'une fenêtre modale ou d'un volet en surimpression (R9, R12), sur le modèle de `ConfirmDialog` :
 *
 *  - à l'activation, le focus entre dans `ref` — sauf s'il y est déjà (champ `autoFocus` d'un enfant) : sur le
 *    conteneur lui-même (`initial="container"`, défaut : la croix ne s'allume pas, le Tab suivant mène au premier
 *    contrôle) ou sur le premier élément tabulable (`initial="first"`) ; le conteneur porte `tabIndex={-1}` ;
 *  - Tab et Maj+Tab bouclent dans le conteneur : rien derrière le voile n'est atteignable ;
 *  - à la désactivation (fermeture, démontage), le focus revient à l'élément qui l'avait avant, s'il est resté
 *    dans le conteneur ou sur le corps de la page.
 *
 *  `active` à faux (volet en ligne sur grand écran) : rien n'est retenu. */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active = true,
  initial: "container" | "first" = "container",
): void {
  useEffect(() => {
    const container = ref.current;
    if (!active || !container || typeof document === "undefined") return;
    const previous = document.activeElement as HTMLElement | null;
    if (!container.contains(document.activeElement)) {
      ((initial === "first" ? focusableIn(container)[0] : undefined) ?? container).focus({ preventScroll: true });
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || e.defaultPrevented) return;
      const items = focusableIn(container);
      if (!items.length) {
        e.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement;
      if (e.shiftKey && (current === first || !container.contains(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !container.contains(current))) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      const now = document.activeElement;
      if (!now || now === document.body || container.contains(now)) previous?.focus?.({ preventScroll: true });
    };
  }, [ref, active, initial]);
}
